import * as THREE from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";

const OPAQUE_OPACITY_THRESHOLD = 0.985;
const TRANSMISSION_THRESHOLD = 0.01;
const DEFAULT_PMREM_SIZE = 128;
const titrationBenchVisibility = new WeakMap<THREE.Object3D, boolean>();

// The imported middle-island power block occludes the experiment flask.
// Keep the fitted laboratory intact outside the focused practical.
export function setTitrationBenchClearance(
  root: THREE.Object3D,
  active: boolean
) {
  root.traverse((object) => {
    if (object.name.replace(/_/g, " ") !== "MASTER Middle Island power 2")
      return;
    if (active) {
      if (!titrationBenchVisibility.has(object)) {
        titrationBenchVisibility.set(object, object.visible);
      }
      object.visible = false;
    } else if (titrationBenchVisibility.has(object)) {
      object.visible = titrationBenchVisibility.get(object)!;
      titrationBenchVisibility.delete(object);
    }
  });
}

type DisposableResource = {
  dispose: () => void;
};

type ShadowMaterial = THREE.Material & {
  alphaMap?: THREE.Texture | null;
  transmission?: number;
};

export type HendersonRenderEnvironment = {
  texture: THREE.Texture;
  dispose: () => void;
};

function materialArray(material: THREE.Mesh["material"]) {
  return Array.isArray(material) ? material : [material];
}

export function isTransparentOrTransmissiveMaterial(material: THREE.Material) {
  const candidate = material as ShadowMaterial;
  return (
    material.transparent ||
    material.opacity < OPAQUE_OPACITY_THRESHOLD ||
    Boolean(candidate.alphaMap) ||
    (candidate.transmission ?? 0) > TRANSMISSION_THRESHOLD
  );
}

export function meshUsesOpaqueShadowTreatment(mesh: THREE.Mesh) {
  const materials = materialArray(mesh.material).filter(Boolean);
  return (
    materials.length > 0 &&
    materials.every(
      (material) =>
        material.visible !== false &&
        !isTransparentOrTransmissiveMaterial(material)
    )
  );
}

export function applyImportedModelRendering(root: THREE.Object3D) {
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    const opaqueShadowTreatment = meshUsesOpaqueShadowTreatment(mesh);
    mesh.castShadow = opaqueShadowTreatment;
    mesh.receiveShadow = opaqueShadowTreatment;
  });
}

export function setImportedModelVisibility(
  root: THREE.Object3D,
  visible: boolean
) {
  root.visible = visible;
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    if (!visible) {
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      return;
    }
    const opaqueShadowTreatment = meshUsesOpaqueShadowTreatment(mesh);
    mesh.castShadow = opaqueShadowTreatment;
    mesh.receiveShadow = opaqueShadowTreatment;
  });
}

export function createOwnedRenderEnvironment(
  texture: THREE.Texture,
  ownedResources: DisposableResource[]
): HendersonRenderEnvironment {
  let disposed = false;
  return {
    texture,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      for (const resource of ownedResources) {
        resource.dispose();
      }
    },
  };
}

export function createHendersonRenderEnvironment(
  renderer: THREE.WebGLRenderer,
  pmremSize = DEFAULT_PMREM_SIZE
) {
  const pmrem = new THREE.PMREMGenerator(renderer);
  let room: RoomEnvironment | null = null;
  try {
    room = new RoomEnvironment();
    const target = pmrem.fromScene(room, 0.04, 0.1, 100, {
      size: pmremSize,
    });
    return createOwnedRenderEnvironment(target.texture, [target, pmrem]);
  } catch (error) {
    pmrem.dispose();
    throw error;
  } finally {
    room?.dispose();
  }
}
